/**
 * Locale-aware date/time formatting.
 *
 * 기존에 "2026년 3월 20일" 형태로 한국어 날짜 포맷이 하드코딩된 자리를
 * UI 언어에 맞춰 변환하기 위한 공용 유틸.
 *
 * 사용: formatReservationDate('2026-03-29', '06:00', beLang)
 *       → ko:  "2026년 03월 29일 06:00"
 *       → en:  "Mar 29, 2026 06:00"
 *       → ja:  "2026年03月29日 06:00"
 *       → zh:  "2026年03月29日 06:00"
 *       → es:  "29 mar 2026 06:00"
 */

import { getCurrentBeLang } from '../contexts/LanguageContext';

const LOCALE_MAP = {
  ko: 'ko-KR',
  en: 'en-US',
  zh: 'zh-CN',
  ja: 'ja-JP',
  es: 'es-ES',
};

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  // 'YYYY-MM-DD' 만 들어오면 JS 가 UTC 자정으로 해석 → KST(+9)에선 같은 날이지만,
  // UTC-1~ 시간대(예: 사모아 -11) 환경에서 -1 day 로 미끄러질 수 있어 defensive 로 local 파싱.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * YYYY-MM-DD + HH:mm 조합 → 현재 UI 언어 포맷 문자열
 */
export function formatReservationDate(dateStr, timeStr, beLangOverride) {
  if (!dateStr) return '-';
  const d = toDate(dateStr);
  if (!d) return '-';
  const beLang = beLangOverride || getCurrentBeLang();
  const locale = LOCALE_MAP[beLang] || 'en-US';
  const datePart = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: '2-digit' }).format(d);
  const timePart = typeof timeStr === 'string' ? timeStr.slice(0, 5) : '';
  return timePart ? `${datePart} ${timePart}` : datePart;
}

/**
 * ISO datetime 또는 Date → 현재 UI 언어 포맷 (날짜만)
 */
export function formatDateLocale(value, beLangOverride) {
  const d = toDate(value);
  if (!d) return '';
  const beLang = beLangOverride || getCurrentBeLang();
  const locale = LOCALE_MAP[beLang] || 'en-US';
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: '2-digit' }).format(d);
}

/**
 * 날짜 + 시간 포함 (분 단위까지)
 */
export function formatDateTimeLocale(value, beLangOverride) {
  const d = toDate(value);
  if (!d) return '';
  const beLang = beLangOverride || getCurrentBeLang();
  const locale = LOCALE_MAP[beLang] || 'en-US';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}
